import {
  arcPath,
  RIM,
  RING_CENTRE,
  RING_NODES,
  RING_RADIUS,
  TIER_ARCS,
  TIER_RADIUS,
  TIERS,
} from './figmaRing';

/**
 * The narrow-viewport Pulse-Omics ring.
 *
 * Same nine nodes and the same twenty chords as the wide one — not a redrawn
 * subset. The design's frame is 1300 wide, and mapping that to a phone puts
 * its 16px labels near 4px, so the drawing has to change scale relative to its
 * type rather than simply shrink.
 *
 * The trick is that only the circle contracts. Labels stay at the design's own
 * 16 units and the box is sized so those render around 11px, then the radius is
 * solved for what fits in what is left. The chords come across unchanged and
 * are scaled about the centre by the same factor with a group transform, so
 * every curve keeps the shape the designer drew.
 */

/**
 * Sized so the 16-unit labels land near 11px in a 390px column. Everything
 * else here is solved against this number, not chosen alongside it.
 */
export const M_CANVAS_W = 567;

/**
 * Solved, not picked, and re-solved whenever the label size moves.
 *
 * Half the box is 283.5. The binding label is Respiratory at 120 degrees: its
 * node sits 0.866R out and its text then reaches SIDE_GAP plus its own eleven
 * characters. At a 15-unit label and an 18-unit gap that leaves room for 186,
 * up from the 165 a 16-unit label and a 25-unit gap allowed — which is the
 * whole trade here, a slightly smaller label buying a meaningfully bigger
 * circle. Nervous and Immune at 80 and 280 degrees are the next constraints
 * and both clear it.
 */
export const M_RADIUS = 186;
export const M_SCALE = M_RADIUS / RING_RADIUS;

/**
 * Clearance between a dot and its label, along the radius.
 *
 * Radial, not horizontal. Offsetting sideways was fine while the dots were the
 * outermost thing on the drawing, but the tier arcs now run 8.9 units outside
 * them — and near the top of the circle the arc is nearly horizontal, so a
 * horizontal offset travels along it instead of away from it. Four labels ended
 * up sitting on the arc. 22 puts the anchor at 208, which clears the arc's
 * outer edge by about the same margin the wide ring has.
 */
const SIDE_GAP = 22;
const ABOVE_GAP = 30;
const BELOW_GAP = 34;

/**
 * Beyond this much horizontal offset a label runs out sideways instead of
 * centring over its dot. Same value the wide ring uses, so a given node is
 * placed the same way at both sizes — at 0.7 the five nodes between were
 * centred here and run out there, which is what put four of them on the arc.
 */
const CENTRE_THRESHOLD = 0.3;

/** Half the cap height of a 16-unit label, for the box the text occupies. */
const LABEL_HALF = 8;

export type MobileNode = {
  label: string;
  dot: { x: number; y: number };
  text: { x: number; y: number; anchor: 'start' | 'middle' | 'end' };
  deep: boolean;
};

/* Each design node reduced to its angle on the circle, which is all this ring
   needs from it — the radius is its own. */
const placed = RING_NODES.map((n) => {
  const rad = Math.atan2(n.dot.x - RING_CENTRE.x, -(n.dot.y - RING_CENTRE.y));
  return { n, sin: Math.sin(rad), cos: Math.cos(rad) };
});

export const M_RIM_R = +(RIM.r * M_SCALE).toFixed(3);
export const M_TIER_R = +(TIER_RADIUS * M_SCALE).toFixed(3);

/* The rim and arcs at this size. The rim is a hairline at any width, so it
   holds its 1.6; the tier arcs come down again from the wide ring's 2.8,
   because the circle they ring is a little over half the size. */
export const M_RIM_WIDTH = 1.4;
export const M_TIER_WIDTH = 2.0;

/**
 * The drawing's vertical extent, so the box closes on the content rather than
 * on a guessed aspect.
 *
 * Three things can be the outermost: a label centred over or under its dot, a
 * side label — which now rides out along the radius, so it reaches further up
 * than the dot it belongs to — and the tier arcs. Missing the middle case cost
 * the top labels 20 units of headroom and put them 2px outside the box.
 */
const labelY = (sin: number, cos: number) =>
  Math.abs(sin) <= CENTRE_THRESHOLD
    ? -M_RADIUS * cos + (cos > 0 ? -ABOVE_GAP : BELOW_GAP)
    : -(M_RADIUS + SIDE_GAP) * cos;

const ARC_REACH = M_TIER_R + M_TIER_WIDTH / 2;
const topMost = Math.min(
  -ARC_REACH,
  ...placed.map(({ sin, cos }) => labelY(sin, cos) - LABEL_HALF),
);
const bottomMost = Math.max(
  ARC_REACH,
  ...placed.map(({ sin, cos }) => labelY(sin, cos) + LABEL_HALF),
);

const V_MARGIN = 18;
export const M_CANVAS_H = Math.round(bottomMost - topMost + V_MARGIN * 2);
export const M_CENTRE = {
  x: M_CANVAS_W / 2,
  y: Math.round(-topMost + V_MARGIN),
};

/**
 * Rounded, because these numbers cross the server/client boundary.
 *
 * Every coordinate here comes out of Math.sin(Math.atan2(...)), and the two
 * engines do not have to agree on the last bit of a transcendental — one node
 * came back as 154.55322720091885 on the client against ...83 from the server,
 * which React reports as a hydration mismatch it will not patch up. Three
 * decimals is far below a device pixel at any size this ring renders.
 */
const r3 = (v: number) => Math.round(v * 1000) / 1000;

export const M_NODES: readonly MobileNode[] = placed.map(({ n, sin, cos }) => {
  const dot = {
    x: r3(M_CENTRE.x + M_RADIUS * sin),
    y: r3(M_CENTRE.y - M_RADIUS * cos),
  };
  if (Math.abs(sin) <= CENTRE_THRESHOLD) {
    return {
      label: n.label,
      dot,
      text: {
        x: dot.x,
        y: r3(cos > 0 ? dot.y - ABOVE_GAP : dot.y + BELOW_GAP),
        anchor: 'middle' as const,
      },
      deep: n.deep,
    };
  }
  const right = sin > 0;
  return {
    label: n.label,
    dot,
    text: {
      x: r3(dot.x + sin * SIDE_GAP),
      y: r3(dot.y - cos * SIDE_GAP),
      anchor: (right ? 'start' : 'end') as 'start' | 'end',
    },
    deep: n.deep,
  };
});

/**
 * The rim and the tier arcs, re-solved at this radius rather than scaled.
 *
 * They could ride the chord group's transform, but their strokes would come
 * down with it — a 3.5-unit arc inside a group at 0.55 is two pixels, and the
 * hairline rim would be under one. Rebuilding them here keeps the design's
 * proportions (the tiers 1.048 of the radius, the labels 1.101) while leaving
 * the weights to be chosen for this size.
 */

/* Same angular spans as the wide ring — the break has to land in the same gap
   between the same two nodes, or the grouping stops meaning anything. */
export const M_TIERS = TIERS.map((t, i) => ({
  ...t,
  d: arcPath(M_CENTRE.x, M_CENTRE.y, M_TIER_R, TIER_ARCS[i]),
}));

/**
 * Maps the design's coordinate space onto this one: scale about the origin,
 * then translate so the two centres coincide. Applied to the chord group as a
 * single transform, which is what lets the exported paths come across
 * untouched.
 */
export const M_CHORD_TRANSFORM = `translate(${(
  M_CENTRE.x -
  M_SCALE * RING_CENTRE.x
).toFixed(3)} ${(M_CENTRE.y - M_SCALE * RING_CENTRE.y).toFixed(
  3,
)}) scale(${M_SCALE.toFixed(5)})`;

/**
 * Stroke widths are divided by the scale so they survive it. A chord drawn at
 * 0.9 would come out at 0.44 units inside a group scaled to 0.49, which is a
 * third of a pixel on a phone — there, but not visible.
 */
/**
 * 1.08 rather than 1.35: the chords come out at 0.8x the weight they were.
 *
 * The boost exists because scaling the chord group down would otherwise thin
 * the strokes with it — a 0.9-unit line inside a group at 0.55 is half a pixel
 * on a phone. But at 1.35 the bundle read heavier than the wide ring's, which
 * on a small canvas turns the centre into a knot rather than a crossing.
 */
export const M_STROKE_BOOST = 1.08 / M_SCALE;

/* 4, down from 6. At the smaller radius the endpoints were reading as blobs
   rather than as points a line arrives at. */
export const M_DOT_R = 4;
/* Two down from 15. The endpoint labels were the loudest thing in a drawing
   whose subject is the bundle between them. */
export const M_LABEL_SIZE = 13;

/* The rim and arcs at this size. The rim is a hairline at any width, so it
   holds its 1.6; the tier arcs come down from 3.5 because the circle they ring
   is a little over half the size. */
